import { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { 
  Loader2, Store, CheckCircle2, XCircle, AlertTriangle, Shield,
  Timer, DollarSign, ChevronDown, ChevronUp, Phone, MessageCircle,
  ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { API_URL } from '../utils/apiConfig';

/**
 * ApprovePurchase - Secure beneficiary approval page
 * 
 * URL: /approve-purchase?token=xxx&request=xxx
 * 
 * This is the page beneficiaries see when they click the approval link
 * from WhatsApp/SMS/email to approve a purchase request from a colmado.
 */
export default function ApprovePurchase() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const token = searchParams.get('token');
  const requestId = searchParams.get('request');
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [requestData, setRequestData] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // 'approved', 'declined', 'expired', 'error'
  const [language, setLanguage] = useState('es'); // Default to Spanish for DR
  const [showDetails, setShowDetails] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Translations
  const t = {
    es: {
      loading: 'Cargando solicitud...',
      title: 'Solicitud de Compra',
      from: 'De',
      orderTotal: 'Total del Pedido',
      availableBefore: 'Saldo Disponible',
      remainingAfter: 'Saldo Después de Compra',
      expiresIn: 'Expira en',
      approve: 'APROBAR COMPRA',
      decline: 'Rechazar',
      approving: 'Aprobando...',
      declining: 'Rechazando...',
      viewDetails: 'Ver detalles',
      hideDetails: 'Ocultar detalles',
      securityNotice: 'Solo aprueba si reconoces esta tienda y el monto.',
      callMerchant: 'Llamar al comercio',
      whatsappMerchant: 'WhatsApp al comercio',
      approved: '¡Compra Aprobada!',
      approvedMessage: 'Tu compra ha sido procesada exitosamente.',
      declined: 'Compra Rechazada',
      declinedMessage: 'Has rechazado esta solicitud de compra.',
      expired: 'Solicitud Expirada',
      expiredMessage: 'Esta solicitud de compra ha expirado. Por favor, pide al comercio que envíe una nueva.',
      alreadyProcessed: 'Ya Procesada',
      alreadyProcessedMessage: 'Esta solicitud ya fue procesada.',
      invalidLink: 'Enlace Inválido',
      invalidLinkMessage: 'Este enlace de aprobación no es válido o ha expirado.',
      error: 'Error',
      errorMessage: 'Algo salió mal. Por favor, intenta de nuevo.',
      homeDelivery: 'Entrega a domicilio',
      inStore: 'Recogida en tienda',
      switchToEnglish: 'English'
    },
    en: {
      loading: 'Loading request...',
      title: 'Purchase Request',
      from: 'From',
      orderTotal: 'Order Total',
      availableBefore: 'Available Balance',
      remainingAfter: 'Balance After Purchase',
      expiresIn: 'Expires in',
      approve: 'APPROVE PURCHASE',
      decline: 'Decline',
      approving: 'Approving...',
      declining: 'Declining...',
      viewDetails: 'View details',
      hideDetails: 'Hide details',
      securityNotice: 'Only approve if you recognize this store and amount.',
      callMerchant: 'Call merchant',
      whatsappMerchant: 'WhatsApp merchant',
      approved: 'Purchase Approved!',
      approvedMessage: 'Your purchase has been processed successfully.',
      declined: 'Purchase Declined',
      declinedMessage: 'You have declined this purchase request.',
      expired: 'Request Expired',
      expiredMessage: 'This purchase request has expired. Please ask the merchant to send a new one.',
      alreadyProcessed: 'Already Processed',
      alreadyProcessedMessage: 'This request has already been processed.',
      invalidLink: 'Invalid Link',
      invalidLinkMessage: 'This approval link is invalid or has expired.',
      error: 'Error',
      errorMessage: 'Something went wrong. Please try again.',
      homeDelivery: 'Home delivery',
      inStore: 'In-store pickup',
      switchToSpanish: 'Español'
    }
  };
  const text = t[language] || t.es;

  // Format currency
  const formatDOP = (amount) => {
    if (!amount && amount !== 0) return 'RD$0';
    return `RD$${parseFloat(amount).toLocaleString('es-DO', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    })}`;
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Fetch request details
  const fetchRequest = useCallback(async () => {
    if (!token || !requestId) {
      setError('invalid_link');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(
        `${API_URL}/api/purchase-requests/review?token=${token}&request=${requestId}`
      );
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to load request');
      }

      if (data.already_processed) {
        setResult(data.status);
        setRequestData(data);
      } else if (data.expired) {
        setResult('expired');
      } else {
        setRequestData(data);
        setCountdown(data.seconds_remaining || 0);
      }
    } catch (err) {
      console.error('Error fetching request:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [token, requestId]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  // Countdown timer
  useEffect(() => {
    if (!requestData || result || countdown <= 0) return;

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          setResult('expired');
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [requestData, result, countdown]);

  // Handle approve
  const handleApprove = async () => {
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/purchase-requests/approve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id: requestId,
          approval_token: token
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResult('approved');
        setRequestData(prev => ({
          ...prev,
          remaining_after_dop: data.remaining_after_dop
        }));
        toast.success(text.approved);
      } else {
        throw new Error(data.detail || 'Approval failed');
      }
    } catch (err) {
      console.error('Approval error:', err);
      toast.error(err.message);
      if (err.message.includes('expired')) {
        setResult('expired');
      }
    } finally {
      setProcessing(false);
    }
  };

  // Handle decline
  const handleDecline = async () => {
    setProcessing(true);
    try {
      const response = await fetch(`${API_URL}/api/purchase-requests/decline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          request_id: requestId,
          approval_token: token
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setResult('declined');
      } else {
        throw new Error(data.detail || 'Decline failed');
      }
    } catch (err) {
      console.error('Decline error:', err);
      toast.error(err.message);
    } finally {
      setProcessing(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 flex items-center justify-center p-4" data-testid="approve-purchase-loading">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 text-purple-600 animate-spin" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900">{text.loading}</h1>
        </div>
      </div>
    );
  }

  // Error/Invalid state
  if (error === 'invalid_link' || error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 flex items-center justify-center p-4" data-testid="approve-purchase-error">
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-red-600" />
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">{text.invalidLink}</h1>
          <p className="text-slate-600">{text.invalidLinkMessage}</p>
        </div>
      </div>
    );
  }

  // Result states
  if (result) {
    const resultConfig = {
      approved: {
        icon: CheckCircle2,
        iconBg: 'bg-emerald-100',
        iconColor: 'text-emerald-600',
        title: text.approved,
        message: text.approvedMessage,
        gradient: 'from-emerald-900 via-emerald-800 to-teal-900'
      },
      declined: {
        icon: XCircle,
        iconBg: 'bg-slate-100',
        iconColor: 'text-slate-600',
        title: text.declined,
        message: text.declinedMessage,
        gradient: 'from-slate-800 via-slate-700 to-slate-800'
      },
      expired: {
        icon: Timer,
        iconBg: 'bg-amber-100',
        iconColor: 'text-amber-600',
        title: text.expired,
        message: text.expiredMessage,
        gradient: 'from-amber-900 via-amber-800 to-orange-900'
      }
    };

    const config = resultConfig[result] || resultConfig.declined;
    const Icon = config.icon;

    return (
      <div className={`min-h-screen bg-gradient-to-br ${config.gradient} flex items-center justify-center p-4`} data-testid={`approve-purchase-${result}`}>
        <div className="bg-white rounded-3xl shadow-2xl p-8 max-w-md w-full text-center">
          <div className={`w-20 h-20 rounded-full ${config.iconBg} flex items-center justify-center mx-auto mb-6`}>
            <Icon className={`w-10 h-10 ${config.iconColor}`} />
          </div>
          <h1 className="text-2xl font-bold text-slate-900 mb-2">{config.title}</h1>
          <p className="text-slate-600 mb-6">{config.message}</p>

          {result === 'approved' && requestData && (
            <div className="bg-emerald-50 border-2 border-emerald-200 rounded-2xl p-6 mb-6">
              <p className="text-emerald-600 text-sm mb-1">{text.orderTotal}</p>
              <p className="text-3xl font-bold text-emerald-800">{formatDOP(requestData.requested_amount_dop)}</p>
              <div className="mt-4 pt-4 border-t border-emerald-200">
                <p className="text-emerald-600 text-sm">{text.remainingAfter}</p>
                <p className="text-xl font-semibold text-emerald-700">{formatDOP(requestData.remaining_after_dop)}</p>
              </div>
            </div>
          )}

          {requestData?.merchant_name && (
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-slate-500 text-sm">{text.from}</p>
              <p className="font-semibold text-slate-800">{requestData.merchant_name}</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Main approval UI
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900" data-testid="approve-purchase-main">
      {/* Language toggle */}
      <div className="absolute top-4 right-4">
        <button
          onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
          className="px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-full text-white text-sm font-medium transition-colors"
          data-testid="language-toggle"
        >
          {language === 'es' ? text.switchToEnglish : t.en.switchToSpanish}
        </button>
      </div>

      <div className="max-w-md mx-auto px-4 py-8 pb-24">
        {/* Logo */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-white tracking-tight">PeCashGlobal</h1>
          <p className="text-purple-200 text-sm mt-1">{text.title}</p>
        </div>

        {/* Main card */}
        <div className="bg-white rounded-3xl shadow-2xl overflow-hidden">
          {/* Countdown banner */}
          {countdown > 0 && (
            <div className="bg-amber-50 border-b border-amber-200 px-4 py-3 flex items-center justify-center gap-2">
              <Timer className="w-4 h-4 text-amber-600" />
              <span className="text-amber-800 text-sm font-medium">{text.expiresIn}</span>
              <span className="font-mono font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded" data-testid="countdown">
                {formatTime(countdown)}
              </span>
            </div>
          )}

          {/* Merchant info */}
          <div className="p-6 border-b border-slate-100">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full bg-purple-100 flex items-center justify-center">
                <Store className="w-7 h-7 text-purple-600" />
              </div>
              <div>
                <p className="text-slate-500 text-sm">{text.from}</p>
                <p className="text-xl font-bold text-slate-900" data-testid="merchant-name">
                  {requestData?.merchant_name}
                </p>
                {requestData?.delivery_type && (
                  <p className="text-sm text-slate-500 mt-0.5">
                    {requestData.delivery_type === 'home_delivery' ? text.homeDelivery : text.inStore}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Amount */}
          <div className="p-6 bg-gradient-to-br from-purple-50 to-white">
            <p className="text-purple-600 text-sm font-medium text-center mb-2">{text.orderTotal}</p>
            <p className="text-5xl font-bold text-purple-900 text-center" data-testid="order-amount">
              {formatDOP(requestData?.requested_amount_dop)}
            </p>
          </div>

          {/* Balance details (expandable) */}
          <div className="border-t border-slate-100">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full px-6 py-3 flex items-center justify-between text-slate-600 hover:bg-slate-50 transition-colors"
              data-testid="toggle-details"
            >
              <span className="text-sm font-medium">{showDetails ? text.hideDetails : text.viewDetails}</span>
              {showDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>

            {showDetails && (
              <div className="px-6 pb-4 space-y-3">
                <div className="flex justify-between items-center p-3 bg-slate-50 rounded-lg">
                  <span className="text-slate-600 text-sm">{text.availableBefore}</span>
                  <span className="font-semibold text-slate-800" data-testid="available-before">
                    {formatDOP(requestData?.available_before_dop)}
                  </span>
                </div>
                <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                  <span className="text-emerald-700 text-sm font-medium">{text.remainingAfter}</span>
                  <span className="font-bold text-emerald-800" data-testid="remaining-after">
                    {formatDOP(requestData?.remaining_after_dop)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Security notice */}
          <div className="px-6 py-3 bg-slate-50 border-t border-slate-100">
            <div className="flex items-start gap-2">
              <Shield className="w-4 h-4 text-slate-400 mt-0.5" />
              <p className="text-xs text-slate-500">{text.securityNotice}</p>
            </div>
          </div>

          {/* Contact merchant buttons */}
          {requestData?.merchant_phone && (
            <div className="px-6 py-3 flex gap-2 border-t border-slate-100">
              <a
                href={`tel:${requestData.merchant_phone}`}
                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-slate-100 hover:bg-slate-200 rounded-lg text-slate-700 text-sm font-medium transition-colors"
                data-testid="call-merchant"
              >
                <Phone className="w-4 h-4" />
                {text.callMerchant}
              </a>
              <a
                href={`https://wa.me/${requestData.merchant_phone.replace(/\D/g, '')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-green-100 hover:bg-green-200 rounded-lg text-green-700 text-sm font-medium transition-colors"
                data-testid="whatsapp-merchant"
              >
                <MessageCircle className="w-4 h-4" />
                WhatsApp
              </a>
            </div>
          )}

          {/* Action buttons */}
          <div className="p-6 space-y-3">
            <Button
              onClick={handleApprove}
              disabled={processing}
              className="w-full h-14 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white text-lg font-bold rounded-2xl shadow-lg shadow-emerald-500/30 transition-all"
              data-testid="approve-btn"
              aria-label={`Approve purchase of ${formatDOP(requestData?.requested_amount_dop)} from ${requestData?.merchant_name || 'merchant'}`}
            >
              {processing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin mr-2" aria-hidden="true" />
                  {text.approving}
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 mr-2" aria-hidden="true" />
                  {text.approve}
                </>
              )}
            </Button>

            <Button
              onClick={handleDecline}
              disabled={processing}
              variant="outline"
              className="w-full h-12 text-slate-600 hover:text-red-600 hover:border-red-200 hover:bg-red-50 rounded-xl transition-all"
              data-testid="decline-btn"
              aria-label={`Decline purchase request from ${requestData?.merchant_name || 'merchant'}`}
            >
              {processing ? text.declining : text.decline}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
